import { MerchantType, TrustBadgeDisplayConfig } from "lib/types/merchant";
import { constants } from "lib/utils/constants";
import { getRefreshToken } from "lib/utils/auth";
import { t } from "i18next";
import {
  CartCustomComponent,
  CartMetadata,
  CheckoutCustomComponent,
  CheckoutMetadata,
  CouponDisplayConfig,
  CtaConfig,
  dialogDisplayConfig,
  InitialCheckoutStepType,
  ItemCardConfigType,
  PrimaryCTAConfigType,
} from "lib/types/merchantConfigTypes";
import { SubscriptionPlanConfig } from "lib/types/subscriptions";
import { getProgressBarConfig, getWishlistConfig } from "./checkout";
import { DEFAULT_PROGRESS_BAR } from "@shopflo/ui/progress-bar";
import { isEmptyObj } from "./helpers";
import { getTextColorByBrightness } from "@shopflo/ui";
import { PAYMENT_CARD_RADIUS_FALLBACK } from "lib/constants/merchant-defaults";

export const parseMerchantData = (response: any, preferredSfContext?: string) => {
  const data = response?.account_attributes?.attributes ?? response?.attributes;
  const preferredSfContextUpper = preferredSfContext?.toUpperCase();
  const logoUrlFromUiConfig =
    preferredSfContextUpper?.length && Array.isArray(response?.ui_config)
      ? response.ui_config.find(
          (c: any) => String(c?.sf_context ?? "").toUpperCase() === preferredSfContextUpper,
        )?.ui_attributes?.logo_url
      : undefined;

  const merchantData: MerchantType = {
    merchantId: response?.uid ?? response?.account_id,
    analytics: response?.analytics ?? {},
    displayName: data?.shop_data?.name ?? "Shopflo Technologies",
    colorPallet: {
      primaryColor:
        response?.essential_ui_attributes?.color_pallet?.primary_color ??
        data?.ui?.color_pallet?.primary_color,
      primaryTextColor:
        response?.essential_ui_attributes?.color_pallet?.primary_text_color ??
        data?.ui?.color_pallet?.primary_text_color,
    },
    couponStackLength: data?.max_discount_stack_length ?? 2,
    logoUrl: logoUrlFromUiConfig?.length
      ? logoUrlFromUiConfig
      : response?.essential_ui_attributes?.logo_url?.length
      ? response?.essential_ui_attributes?.logo_url
      : data?.ui?.logo_url?.length
      ? data?.ui?.logo_url
      : data?.logo_url,
    isDiscountEnabled:
      response?.essential_ui_attributes?.is_discounts_enabled ?? data?.ui?.is_discounts_enabled ?? true,
    prepaidDiscounts: {
      type: data?.prepaid_discounts?.type,
      value: data?.prepaid_discounts?.value,
      limit: data?.prepaid_discounts?.limit,
      enabled: data?.prepaid_discounts?.enabled,
    },
    addressConfig: {
      isGSTInputEnabled: data?.address_config?.gst_input_enabled,
      isBillingAddressEnabled: data?.address_config?.billing_address_enabled ?? false,
      isInternationalAddressEnabled: data?.address_config?.international_address_enabled,
      deliveryConfig: {
        isEnabled: data?.address_config?.delivery_config?.enabled,
        deliveryText: data?.address_config?.delivery_config?.delivery_text,
        isETDEnabled: data?.address_config?.delivery_config?.is_etd_enabled ?? false,
      },
      codConfig: {
        verifyCod: data?.address_config?.cod_config?.verify_cod,
      },
      disable_unavailable_pincode: data?.address_config?.disable_unavailable_pincode ?? false,
      enabledCountries: data?.address_config?.enabled_countries ?? [],
    },
    discountSettings: {
      showMaxDiscount: data?.discounts?.max_discount_enabled ?? true,
    },
    brandUrl: data?.website_url,
    isTwoStepCheckout: data?.metadata?.is_two_step_checkout ?? false,
    showOriginalPrice: data?.metadata?.show_original_price ?? false,
    wishlistConfig: getWishlistConfig(data?.wishlist),
    marketingContextText: data?.marketing_consent_text ?? "",
    salesCommsContext: data?.sales_comms_context ?? 1,
    codVerification: data?.cod_verification ?? "POST_COD",
    domain: data?.shop_data?.domain,
    forceCustomThankYouPage: data?.force_custom_thank_you_page ?? false,
    disableThirdPartyCookieRedirect: data?.disable_third_party_cookie_redirect ?? false,
    upiTileWithIcons: data?.upi_tile_with_icons ?? false,
    hideCODpopup: data?.hide_cod_popup ?? false,
    showShippingOnPayments: data?.show_shipping_on_popup ?? false,
    maxQuantityPerItem: data?.max_quantity_per_item,
    isMarketingPopupEnabled: data?.new_sso_popup_enabled ?? false,
    immutablePriceBand: {
      minPrice: data?.immutable_price_band?.min_price,
      maxPrice: data?.immutable_price_band?.max_price,
    },
    splitCod: {
      excludeCodCharges: data?.split_cod?.exclude_cod_charges ?? false,
    },
    hideShippingMaxDays: data?.hide_shipping_max_days ?? false,
    customCartCheckoutEnabled: data?.custom_cart_checkout_enabled ?? false,
    hideCartTotal: data?.hide_cart_total ?? false,
    cartNote: {
      enabled: data?.cart_note?.enabled ?? false,
      title: data?.cart_note?.metadata?.title ?? "",
      suggestionText: data?.cart_note?.metadata?.suggestion_text ?? "",
      icon: data?.cart_note?.metadata?.icon ?? "EDIT",
    },
    disableMarketingConsent: Boolean(data?.disable_marketing_consent),
    isDebugLoggingEnabled: data?.debug_logging_enabled ?? false,
    automationEnabled: data?.automation_enabled ?? false,
    isOnePageCheckout: data?.is_one_page_checkout ?? false,
    upsellStrikeThroughConfig: {
      showUpsellCompareAtPrice: data?.upsell_strike_through_config?.show_upsell_compare_at_price ?? false,
      compareWithOriginalPriceOnDiscounted:
        data?.upsell_strike_through_config?.compare_with_original_price_on_discounted ?? false,
      hideCompareAtPriceOnDiscounted:
        data?.upsell_strike_through_config?.hide_compare_at_price_on_discounted ?? false,
    },
    shopData: {
      domain: data?.shop_data?.domain,
    },
    enableLogging: data?.enable_logging ?? false,
    bureauEnabled: data?.bureau_enabled ?? false,
    addressAutocompleteEnabled: data?.address_auto_complete_enabled ?? false,
    captchaProvider: data?.captcha_provider ?? "TURNSTILE",
    migrationConfig: {
      kratosOrderFlow: data?.migration_config?.kratos_order_flow ?? false,
      kratosPaymentFlow: data?.migration_config?.kratos_payment_flow ?? false,
    },
    subscriptionsEnabled: data?.subscriptions_enabled ?? false,
    sso: {
      oidcProviderEnabled: data?.sso?.oidc_provider_enabled,
    },
    cart: {
      skipLoginPopup: data?.cart?.skip_login_popup
    }
  };
  return merchantData;
};

export const getIsTwoStepCheckout = (data: any) => {
  const isTwoStepEnabled = Boolean(data?.metadata?.is_two_step_checkout);

  return !Boolean(getRefreshToken()) && isTwoStepEnabled;
};

const getPrimaryCtaConfig = (data: any) => {
  const defaultCtaConfig: CtaConfig[] = [
    { type: "AUTH", ctaText: t("add_address") },
    { type: "ORDER_SUMMARY", ctaText: t("proceed_to_pay") },
    { type: "PAYMENTS", ctaText: "" },
  ];
  const config = data?.primary_cta_config;
  if (!Array.isArray(config) || !Boolean(config)) return defaultCtaConfig;
  let primaryCtaConfig: CtaConfig[] = [];
  config.forEach((value: any) => {
    const ctaValue: CtaConfig = {
      type: value?.type,
      ctaText: Boolean(value?.cta_text)
        ? value?.cta_text
        : defaultCtaConfig.find((type) => type === value?.type)?.ctaText,
    };
    primaryCtaConfig.push(ctaValue);
  });
  return primaryCtaConfig;
};

export const getUIPrimaryCtaConfig = (data: any) => {
  const config = data?.primary_cta_config;
  if (!Array.isArray(config)) return;
  if (!Boolean(config)) return;
  let primaryCtaConfig: PrimaryCTAConfigType[] = [];
  config.forEach((value: any) => {
    const primaryCtaValue: PrimaryCTAConfigType = {
      type: value.type,
      ctaText: value.cta_text,
      backgroundColor: value.background_color,
      textColor: value.text_color,
      borderRadius: value.border_radius ?? 12,
      showUpiIcons: value.show_upi_icons ?? false,
    };
    primaryCtaConfig.push(primaryCtaValue);
  });
  return primaryCtaConfig;
};

const getUITrustBadgeConfig = (config: any): TrustBadgeDisplayConfig => {
  const defaultConfig: TrustBadgeDisplayConfig = {
    type: "AUTH",
    showBadges: false,
    badges: [],
  };
  if (!Boolean(config)) return defaultConfig;
  const badges = config.badges?.filter((badge: any) => badge.isActive);
  return {
    type: config?.type ?? "AUTH",
    showBadges: Boolean(config?.showBadges),
    badges: badges ?? [],
  };
};

const getEarliestDeliveryText = (minDays: number) => {
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + minDays);

  const weekday = deliveryDate.toLocaleDateString("en-US", { weekday: "short" });
  const day = deliveryDate.getDate();
  const month = deliveryDate.toLocaleDateString("en-US", { month: "short" });

  return `Earliest delivery by ${weekday}, ${day} ${month}`;
};

export const getETDText = (data: any) => {
  const minDays = data?.min_days_to_deliver ? parseInt(data?.min_days_to_deliver) : 4;
  const earliestDeliveryText = getEarliestDeliveryText(minDays);
  return earliestDeliveryText;
};

export const getDeliveryInformation = (
  minDays: number,
  maxDays: number,
  etdText: string,
  minDaysOnly: boolean,
) => {
  const additionalText = etdText?.trim() ?? "";

  if (!Boolean(minDays) && !Boolean(maxDays)) return additionalText || null;

  const getDayText = (days: number) => (days === 1 ? "day" : "days");

  if (minDaysOnly && Boolean(minDays)) {
    return getEarliestDeliveryText(minDays);
  }

  const label = additionalText || "Delivery information:";
  const daysText = `${minDays} - ${maxDays} ${getDayText(maxDays)}`;
  return `${label} ${minDays ? daysText : `${maxDays} ${getDayText(maxDays)}`}`;
};

const getCouponDisplayConfig = (data: any, defaultConfig: CouponDisplayConfig[]) => {
  const config = data?.coupon_visibility_config;
  if (!Array.isArray(config) || !Boolean(config)) return defaultConfig;
  const couponDisplayConfig = config.reduce((acc: CouponDisplayConfig[], value: any) => {
    const couponConfig: CouponDisplayConfig = {
      type: value?.type,
      // TODO: handle for visible and visibile...............ugggh
      isDisplayEnabled: value?.visible || value?.visibile,
      displayType: value?.visibility_extent === "codeEntry" ? "INPUT" : "STRIP",
      hideSavings: value?.hide_savings ?? false,
    };
    acc.push(couponConfig);
    return acc;
  }, []);
  return couponDisplayConfig;
};

export const getCheckoutCouponDisplayConfig = (data: any) => {
  const defaultCouponDisplayConfig: CouponDisplayConfig[] = [
    {
      type: "AUTH",
      isDisplayEnabled: true,
      displayType: "INPUT",
      hideSavings: false,
    },
    {
      type: "ORDER_SUMMARY",
      isDisplayEnabled: true,
      displayType: "INPUT",
      hideSavings: false,
    },
    {
      type: "PAYMENTS",
      isDisplayEnabled: true,
      displayType: "INPUT",
      hideSavings: false,
    },
  ];
  return getCouponDisplayConfig(data, defaultCouponDisplayConfig);
};

export const getCartCouponDisplayConfig = (data: any) => {
  const defaultCartCouponDisplayConfig: CouponDisplayConfig[] = [
    {
      type: "CART",
      isDisplayEnabled: true,
      displayType: "STRIP",
      hideSavings: false,
    },
  ];
  // handle for cart in case we don't have any configuration for cart
  const couponDisplayConfig = getCouponDisplayConfig(data, defaultCartCouponDisplayConfig);
  if (!Boolean(couponDisplayConfig.find((item) => item.type === "CART"))) {
    couponDisplayConfig.push(defaultCartCouponDisplayConfig[0]);
  }
  return couponDisplayConfig;
};

export const parseCheckoutUIMetadata = (values: any) => {
  if (!values || isEmptyObj(values)) return;
  const checkoutUIMetadata = {
    couponDisplayConfig: getCheckoutCouponDisplayConfig(values),
  };
  return { ...checkoutUIMetadata };
};

export const parseCartUIMetadata = (values: any) => {
  if (!values || isEmptyObj(values)) return;
  const cartUIMetadata = {
    couponDisplayConfig: getCartCouponDisplayConfig(values),
  };
  return { ...cartUIMetadata };
};

/**
 * Line item layout, backend driven via `ui_attributes.item_card.align`. Anything other than an
 * explicit "row" falls back to the legacy stacked layout.
 */
const getItemCardConfig = (uiAttributes: any): ItemCardConfigType => ({
  align: uiAttributes?.item_card?.align === "row" ? "row" : "column",
});

export const updateCartMetadata = (data: any): Omit<CartMetadata, "metadataLoading"> => {
  return {
    progressBarConfig: data?.ui_attributes?.progress_bar_attributes
      ? getProgressBarConfig(data?.ui_attributes?.progress_bar_attributes)
      : DEFAULT_PROGRESS_BAR,
    primaryCTAConfig: getUIPrimaryCtaConfig(data?.ui_attributes)?.find(
      (config: PrimaryCTAConfigType) => config.type === "CART",
    ),
    couponDisplayConfig: getCartCouponDisplayConfig(data?.ui_attributes),
    uiAttributes: {
      otpConfig: {
        whatsappOtpEnabled: data?.ui_attributes?.otp_config?.whatsapp_otp_enabled ?? false,
        guestCheckoutEnabled: data?.ui_attributes?.otp_config?.guest_checkout_enabled ?? false,
      },
      showUpiIconsOnCartButton: data?.ui_attributes?.show_upi_icons_on_cart_button ?? false,
      layout: {
        hiddenElements: data?.ui_attributes?.layout?.hidden_elements ?? [],
        availableElements: data?.ui_attributes?.layout?.available_elements ?? null,
        enableMarketingFlag: data?.ui_attributes?.layout?.enable_marketing_flag ?? false,
        metadata: {
          showOriginalPrice: data?.ui_attributes?.layout?.metadata?.show_original_price ?? false,
          showAllItemProperties: data?.ui_attributes?.layout?.metadata?.show_all_item_properties ?? false,
          showItemPropertyKey: data?.ui_attributes?.layout?.metadata?.show_item_property_key ?? false,
        },
      },
      marketingConsentText: data?.ui_attributes?.marketing_consent_text ?? "",
      itemProperties: data?.ui_attributes?.item_properties ?? [],
      celebrationPopupConfig: {
        celebrationsTotalSavingsBtnText:
          data?.ui_attributes?.celebration_popup_config?.total_savings_btn_text,
        freebieAppliedCelebrationsText:
          data?.ui_attributes?.celebration_popup_config?.freebie_applied_celebrations_text,
        freeGiftsText: data?.ui_attributes?.celebration_popup_config?.free_gifts_text,
      },
      bannerConfig: buildBannerConfig(
        data?.ui_attributes?.banner_config,
        data?.ui_attributes?.banner_settings,
      ),
      bottomBannerConfig: (data?.ui_attributes?.bottom_banner_config ?? []).map((b: any) => ({
        type: b?.type ?? "",
        enabled: b?.enabled ?? false,
        multipleBannersEnabled: false,
        backgroundColor: b?.background_color ?? "",
        textColor: b?.text_color ?? "",
        htmlTextString: b?.html_text_string ?? "",
        banners: [
          {
            backgroundColor: b?.background_color ?? "",
            textColor: b?.text_color ?? "",
            htmlTextString: b?.html_text_string ?? "",
          },
        ],
      })),
      page: {
        backgroundColor: data?.ui_attributes?.page?.background_color ?? "#FFFFFF",
      },
      header: {
        backgroundColor: data?.ui_attributes?.header?.background_color ?? "#FFFFFF",
        textColor: data?.ui_attributes?.header?.text_color ?? "#000000",
      },
      footer: {
        backgroundColor: data?.ui_attributes?.footer?.background_color ?? "#FFFFFF",
        textColor: data?.ui_attributes?.footer?.text_color ?? "#000000",
        savingsColor: data?.ui_attributes?.footer?.savings_color ?? "#2C874A",
        showSavings: data?.ui_attributes?.footer?.show_savings ?? true,
      },
      addonConfig: {
        backgroundColor: data?.ui_attributes?.addon_config?.background_color ?? "#f2f2f3",
        textColor: data?.ui_attributes?.addon_config?.text_color ?? "#3f3f46",
        buttonColor: data?.ui_attributes?.addon_config?.button_color ?? "",
      },
      orderSummaryConfig: {
        backgroundColor: data?.ui_attributes?.order_summary_config?.background_color ?? "#f4f4f5",
        borderColor: data?.ui_attributes?.order_summary_config?.border_color ?? "#FFFFFF",
        offerBadgeColor: data?.ui_attributes?.order_summary_config?.offer_badge_color ?? "#2C874A",
        textColor: data?.ui_attributes?.order_summary_config?.text_color ?? "#3F3F46",
        quantityEditorBackground:
          data?.ui_attributes?.order_summary_config?.quantity_editor_background ?? "#FFFFFF",
        quantityEditorText: data?.ui_attributes?.order_summary_config?.quantity_editor_text ?? "#4D4D4D",
        cardRadius: data?.ui_attributes?.order_summary_config?.card_radius ?? 12,
        cardBorderWidth: data?.ui_attributes?.order_summary_config?.card_border_width ?? 0,
        cardBorderSides: data?.ui_attributes?.order_summary_config?.card_border_sides ?? "all",
      },
      itemCardConfig: getItemCardConfig(data?.ui_attributes),
      notesConfig: {
        isEnabled: data?.ui_attributes?.notes_config?.is_enabled ?? false,
        backgroundColor: data?.ui_attributes?.notes_config?.background_color ?? "#FFFFFF",
        borderColor: data?.ui_attributes?.notes_config?.border_color ?? "#E4E4E7",
        textColor: data?.ui_attributes?.notes_config?.text_color ?? "#000000",
        buttonColor: data?.ui_attributes?.notes_config?.button_color ?? "#2C874A",
        cardRadius: data?.ui_attributes?.notes_config?.card_radius ?? 12,
        cardBorderWidth: data?.ui_attributes?.notes_config?.card_border_width ?? 1,
        cardBorderSides: data?.ui_attributes?.notes_config?.card_border_sides ?? "all",
      },
      discountConfig: {
        callout: {
          backgroundColor: data?.ui_attributes?.discount_config?.callout?.background_color ?? "#FFFFFF",
          infoColor: data?.ui_attributes?.discount_config?.callout?.info_color ?? "#4d4d4d",
          primaryColor: data?.ui_attributes?.discount_config?.callout?.primary_color ?? "#2c874a",
          borderColor: data?.ui_attributes?.discount_config?.callout?.border_color ?? "#05966933",
        },
        footer: {
          backgroundColor: data?.ui_attributes?.discount_config?.footer?.background_color ?? "#2c874a0d",
          infoColor: data?.ui_attributes?.discount_config?.footer?.info_color ?? "#3f3f46b3",
          primaryColor: data?.ui_attributes?.discount_config?.footer?.primary_color ?? "#2c874a",
        },
        input: {
          backgroundColor: data?.ui_attributes?.discount_config?.input?.background_color ?? "#FFFFFF",
          borderColor: data?.ui_attributes?.discount_config?.input?.border_color ?? "#E4E4E7",
          primaryColor: data?.ui_attributes?.discount_config?.input?.primary_color ?? "#6e6e6e",
        },
        button: {
          backgroundColor: data?.ui_attributes?.discount_config?.button?.background_color ?? "#000000",
          textColor: data?.ui_attributes?.discount_config?.button?.text_color ?? "#FFFFFF",
        },
        totalSavings: {
          primaryColor: data?.ui_attributes?.discount_config?.total_savings?.primary_color ?? "#eab308",
          infoColor: data?.ui_attributes?.discount_config?.total_savings?.info_color ?? "#3f3f46",
          backgroundColor: data?.ui_attributes?.discount_config?.total_savings?.background_color ?? "#FFFFFF",
        },
        cardRadius: data?.ui_attributes?.discount_config?.card_radius ?? 12,
        cardBorderWidth: data?.ui_attributes?.discount_config?.card_border_width ?? 1,
        cardBorderSides: data?.ui_attributes?.discount_config?.card_border_sides ?? "all",
      },
      elementOrder: data?.ui_attributes?.element_order ?? ["ORDER_SUMMARY", "DISCOUNTS", "UPSELL", "NOTES"],
      upsell: {
        discountedUpsellTitle: data?.ui_attributes?.upsell?.discounted_upsell_title ?? "",
        isEnabled: data?.ui_attributes?.upsell?.is_enabled ?? false,
        upsellTitle: data?.ui_attributes?.upsell?.upsell_title ?? "",
        shouldOverrideDiscountedUpsell:
          data?.ui_attributes?.upsell?.should_override_discounted_upsell ?? false,
        backgroundColor: data?.ui_attributes?.upsell?.background_color ?? "#FFFFFF",
        borderColor: data?.ui_attributes?.upsell?.border_color ?? "#E4E4E7",
        textColor: data?.ui_attributes?.upsell?.text_color ?? "#4d4d4d",
        buttonColor: data?.ui_attributes?.upsell?.button_color ?? "#2c874a",
        offerBadgeColor: data?.ui_attributes?.upsell?.offer_badge_color ?? "#2c874a",
        titleColor: data?.ui_attributes?.upsell?.title_color ?? "#2b874a",
        cardRadius: data?.ui_attributes?.upsell?.card_radius ?? 12,
        cardBorderWidth: data?.ui_attributes?.upsell?.card_border_width ?? 1,
        cardBorderSides: data?.ui_attributes?.upsell?.card_border_sides ?? "all",
        clearUpsellItemsOnCartClose: data?.ui_attributes?.upsell?.clear_upsell_items_on_cart_close ?? false,
      },
      recentlyViewedConfig: {
        // Cart respects merchant config; empty cart always treats recently viewed as enabled
        // (see UpSellSection EMPTY_CART override).
        enabled: data?.ui_attributes?.recently_viewed_config?.enabled ?? false,
        minItems: data?.ui_attributes?.recently_viewed_config?.min_items ?? 1,
        maxItems: data?.ui_attributes?.recently_viewed_config?.max_items ?? 5,
        page: data?.ui_attributes?.recently_viewed_config?.page ?? ["EMPTY_CART", "CART"],
        tabTitle: data?.ui_attributes?.recently_viewed_config?.tab_title ?? "",
        backgroundColor:
          data?.ui_attributes?.recently_viewed_config?.background_color ??
          data?.ui_attributes?.upsell?.background_color ??
          "#FFFFFF",
        borderColor:
          data?.ui_attributes?.recently_viewed_config?.border_color ??
          data?.ui_attributes?.upsell?.border_color ??
          "#E4E4E7",
        textColor:
          data?.ui_attributes?.recently_viewed_config?.text_color ??
          data?.ui_attributes?.upsell?.text_color ??
          "#4d4d4d",
        buttonColor:
          data?.ui_attributes?.recently_viewed_config?.button_color ??
          data?.ui_attributes?.upsell?.button_color ??
          "#2c874a",
        offerBadgeColor:
          data?.ui_attributes?.recently_viewed_config?.offer_badge_color ??
          data?.ui_attributes?.upsell?.offer_badge_color ??
          "#2c874a",
        titleColor:
          data?.ui_attributes?.recently_viewed_config?.title_color ??
          data?.ui_attributes?.upsell?.title_color ??
          "#2b874a",
        cardRadius:
          data?.ui_attributes?.recently_viewed_config?.card_radius ??
          data?.ui_attributes?.upsell?.card_radius ??
          12,
        cardBorderWidth:
          data?.ui_attributes?.recently_viewed_config?.card_border_width ??
          data?.ui_attributes?.upsell?.card_border_width ??
          1,
        cardBorderSides:
          data?.ui_attributes?.recently_viewed_config?.card_border_sides ??
          data?.ui_attributes?.upsell?.card_border_sides ??
          "all",
      },
      couponPageConfig: {
        backgroundColor: data?.ui_attributes?.coupon_page_config?.background_color ?? "#FFFFFF",
        textColor: data?.ui_attributes?.coupon_page_config?.text_color ?? "#4d4d4d",
        primaryColor: data?.ui_attributes?.coupon_page_config?.primary_color,
        cardRadius: data?.ui_attributes?.coupon_page_config?.card_radius ?? 6,
        filterRadius: data?.ui_attributes?.coupon_page_config?.filter_radius ?? 6,
        buttonRadius: data?.ui_attributes?.coupon_page_config?.button_radius ?? 6,
        inputRadius: data?.ui_attributes?.coupon_page_config?.input_radius ?? 10,
      },
      partnerOffers: {
        enabled: data?.ui_attributes?.partner_offers?.enabled ?? false,
        section_title: data?.ui_attributes?.partner_offers?.section_title ?? "Partner Offers",
        branding: data?.ui_attributes?.partner_offers?.branding,
      },
      emptyCartUpsellEnabled: data?.ui_attributes?.empty_cart_upsell_enabled ?? false,
      emptyCartCtaText: data?.ui_attributes?.empty_cart_cta_text ?? "",
      emptyCartCtaUrl: data?.ui_attributes?.empty_cart_cta_url ?? "",
      primaryCtaDisabilityRule: data?.ui_attributes?.primary_cta_disability_rule ?? "",
      jsonLogicRules: {
        loginButtonCtaRule: data?.ui_attributes?.json_logic_rules?.login_button_cta_rule ?? "",
      },
      itemModificationRule: data?.ui_attributes?.item_modification_rule ?? {},
      upsellPreview: data?.ui_attributes?.upsell_preview ?? false,
      customComponentsConfig: {
        components: (data?.ui_attributes?.custom_component_config?.custom_components ?? []).map(
          (c: any): CartCustomComponent => ({
            position: c?.position ?? "NONE",
            enabled: c?.enabled ?? false,
            htmlTextString: c?.html_text_string ?? "",
          }),
        ),
      },
      dialogConfig: {
        dialogs: (data?.ui_attributes?.dialog_display_config?.dialogs ?? []).map(
          (d: any): dialogDisplayConfig => ({
            enabled: d?.enabled ?? false,
            key: d?.key ?? "",
            weight: d?.weight ?? 0,
            delay: d?.delay ?? 0,
            duration: d?.duration ?? 0,
            metadata: d?.metadata ?? {},
          }),
        ),
      },
    },
  };
};

export const updateCheckoutMetadata = (data: any): Omit<CheckoutMetadata, "metadataLoading"> => {
  return {
    uiAttributes: {
      couponPageConfig: {
        backgroundColor: data?.ui_attributes?.coupon_page_config?.background_color ?? "#FFFFFF",
        textColor: data?.ui_attributes?.coupon_page_config?.text_color,
        primaryColor: data?.ui_attributes?.coupon_page_config?.primary_color,
        cardRadius: data?.ui_attributes?.coupon_page_config?.card_radius ?? 6,
        filterRadius: data?.ui_attributes?.coupon_page_config?.filter_radius ?? 6,
        buttonRadius: data?.ui_attributes?.coupon_page_config?.button_radius ?? 6,
        inputRadius: data?.ui_attributes?.coupon_page_config?.input_radius ?? 10,
      },
      layout: {
        itemsMutable: data?.ui_attributes?.layout?.items_mutable ?? false,
        orderSummaryStyle: data?.ui_attributes?.layout?.order_summary_style ?? "HEADER",
        hiddenElements: data?.ui_attributes?.layout?.hidden_elements ?? [],
        availableElements: data?.ui_attributes?.layout?.available_elements ?? null,
        enableMarketingFlag: data?.ui_attributes?.layout?.enable_marketing_flag ?? false,
        metadata: {
          isOrderSummaryOpen: data?.ui_attributes?.layout?.metadata?.is_order_summary_open ?? false,
          showOrderSummaryTotal: data?.ui_attributes?.layout?.metadata?.show_order_summary_total ?? false,
          showOriginalPrice: data?.ui_attributes?.layout?.metadata?.show_original_price ?? false,
          showAllItemProperties: data?.ui_attributes?.layout?.metadata?.show_all_item_properties ?? false,
          showItemPropertyKey: data?.ui_attributes?.layout?.metadata?.show_item_property_key ?? false,
        },
      },
      deliveryConfig: {
        deliveryText: data?.ui_attributes?.delivery_config?.delivery_text ?? "",
        isEnabled: data?.ui_attributes?.delivery_config?.enabled ?? false,
        isETDEnabled: data?.ui_attributes?.delivery_config?.is_etd_enabled ?? false,
      },
      marketingConsentText: data?.ui_attributes?.marketing_consent_text ?? "",
      itemProperties: data?.ui_attributes?.item_properties ?? [],
      urgencyNudgeConfig: data?.ui_attributes?.urgency_nudge_config
        ? {
            title: data?.ui_attributes?.urgency_nudge_config?.title ?? "",
            textColor: data?.ui_attributes?.urgency_nudge_config?.text_color ?? "#000000",
            fomoMessage: data?.ui_attributes?.urgency_nudge_config?.fomo_message ?? "",
          }
        : undefined,
      celebrationPopupConfig: {
        celebrationsTotalSavingsBtnText:
          data?.ui_attributes?.celebration_popup_config?.total_savings_btn_text,
        freebieAppliedCelebrationsText:
          data?.ui_attributes?.celebration_popup_config?.freebie_applied_celebrations_text,
        freeGiftsText: data?.ui_attributes?.celebration_popup_config?.free_gifts_text,
      },
      trustBadgeConfig: getUITrustBadgeConfig(data?.ui_attributes?.trust_badges),
      exitCheckoutAction: data?.ui_attributes?.exit_checkout_action ?? "none",
      upsell: {
        discountedUpsellTitle: data?.ui_attributes?.upsell?.discounted_upsell_title ?? "",
        isEnabled: data?.ui_attributes?.upsell?.is_enabled ?? false,
        upsellTitle: data?.ui_attributes?.upsell?.upsell_title ?? "",
        shouldOverrideDiscountedUpsell:
          data?.ui_attributes?.upsell?.should_override_discounted_upsell ?? false,
      },
      discountBankOffers: data?.ui_attributes?.discount_bank_offers ?? false,
      partnerOffers: {
        enabled: data?.ui_attributes?.partner_offers?.enabled ?? false,
        section_title: data?.ui_attributes?.partner_offers?.section_title ?? "Partner Offers",
        branding: data?.ui_attributes?.partner_offers?.branding,
      },
      discountConfig: {
        callout: {
          backgroundColor: data?.ui_attributes?.discount_config?.callout_background_color ?? "#FFFFFF",
          infoColor: data?.ui_attributes?.discount_config?.callout_info_color ?? "#4d4d4d",
          primaryColor: data?.ui_attributes?.discount_config?.callout_primary_color ?? "#2c874a",
          borderColor: data?.ui_attributes?.discount_config?.callout_border_color ?? "#2c874a33",
        },
        footer: {
          backgroundColor: data?.ui_attributes?.discount_config?.footer_background_color ?? "#2c874a0d",
          infoColor: data?.ui_attributes?.discount_config?.footer_info_color ?? "#3f3f46b3",
          primaryColor: data?.ui_attributes?.discount_config?.footer_primary_color ?? "#2c874a",
        },
        input: {
          backgroundColor: data?.ui_attributes?.discount_config?.input_background_color ?? "#FFFFFF",
          borderColor: data?.ui_attributes?.discount_config?.input_border_color ?? "#E4E4E7",
          primaryColor: data?.ui_attributes?.discount_config?.input_primary_color ?? "#6e6e6e",
        },
        button: {
          backgroundColor: data?.ui_attributes?.discount_config?.button_background_color ?? "#000000",
          textColor: data?.ui_attributes?.discount_config?.button_text_color ?? "#FFFFFF",
        },
        totalSavings: {
          primaryColor: data?.ui_attributes?.discount_config?.total_savings_primary_color ?? "#eab308",
          infoColor: data?.ui_attributes?.discount_config?.total_savings_info_color ?? "#3f3f46",
          backgroundColor: data?.ui_attributes?.discount_config?.total_savings_background_color ?? "#FFFFFF",
        },
        cardRadius: data?.ui_attributes?.discount_config?.card_radius ?? 12,
        cardBorderWidth: data?.ui_attributes?.discount_config?.card_border_width ?? 1,
        cardBorderSides: data?.ui_attributes?.discount_config?.card_border_sides ?? "all",
      },
      logoUrl: data?.ui_attributes?.logo_url ?? "",
      bannerConfig: buildBannerConfig(
        data?.ui_attributes?.banner_config,
        data?.ui_attributes?.banner_settings,
      ),
      shippingListExpansion: data?.ui_attributes?.shipping_list_expansion ?? false,
      paymentTileConfig: getPaymentTileConfig(data?.ui_attributes?.payment_tile_config),
      subscriptionPlanConfig: getSubscriptionPlanConfig(data?.ui_attributes?.subscription_config),
      upsellPreview: data?.ui_attributes?.upsell_preview ?? false,
      jsonLogicRules: {
        loginButtonCtaRule: data?.ui_attributes?.json_logic_rules?.login_button_cta_rule ?? "",
      },
      customComponentsConfig: {
        components: (data?.ui_attributes?.custom_component_config?.custom_components ?? []).map(
          (c: any): CheckoutCustomComponent => ({
            type: c?.type ?? "",
            position: c?.position ?? "NONE",
            enabled: c?.enabled ?? false,
            htmlTextString: c?.html_text_string ?? "",
          }),
        ),
      },
      dialogConfig: {
        dialogs: (data?.ui_attributes?.dialog_display_config?.dialogs ?? []).map(
          (d: any): dialogDisplayConfig => ({
            enabled: d?.enabled ?? false,
            key: d?.key ?? "",
            weight: d?.weight ?? 0,
            delay: d?.delay ?? 0,
            duration: d?.duration ?? 0,
            metadata: d?.metadata ?? {},
          }),
        ),
      },
      itemCardConfig: getItemCardConfig(data?.ui_attributes),
      convenienceFeeConfig: data?.ui_attributes?.convenience_fee_config,
      exitCouponNudgeConfig: data?.ui_attributes?.exit_coupon_nudge_config
        ? {
            badge: {
              bgColor: data.ui_attributes.exit_coupon_nudge_config?.badge?.bg_color ?? "#000000",
              textColor: data.ui_attributes.exit_coupon_nudge_config?.badge?.text_color ?? "#FFFFFF",
              htmlText: data.ui_attributes.exit_coupon_nudge_config?.badge?.html_text_string ?? "",
            },
            nudge: {
              bgColor: data.ui_attributes.exit_coupon_nudge_config?.nudge?.bg_color ?? "#FFFFFF",
              textColor: data.ui_attributes.exit_coupon_nudge_config?.nudge?.text_color ?? "#000000",
              htmlText: data.ui_attributes.exit_coupon_nudge_config?.nudge?.html_text_string ?? "",
            },
            ctaColor:
              data.ui_attributes.exit_coupon_nudge_config?.cta_color ??
              data.ui_attributes.exit_coupon_nudge_config?.nudge?.bg_color ??
              "#18181B",
            couponValue: data.ui_attributes.exit_coupon_nudge_config?.coupon_value ?? "",
            additionalSurvey: data.ui_attributes.exit_coupon_nudge_config?.additional_survey_enabled ?? false,
            couponCode: data.ui_attributes.exit_coupon_nudge_config?.coupon_code,
          }
        : undefined,
      otpConfig: {
        whatsappOtpEnabled: data?.ui_attributes?.otp_config?.whatsapp_otp_enabled ?? false,
        guestCheckoutEnabled: data?.ui_attributes?.otp_config?.guest_checkout_enabled ?? false,
      },
    },
    initialCheckoutStep:
      data?.default_view === "PAYMENTS"
        ? ("PAYMENTS" as InitialCheckoutStepType)
        : ("ADDRESS" as InitialCheckoutStepType),
    couponDisplayConfig: getCheckoutCouponDisplayConfig(data?.ui_attributes),
    primaryCTAConfig: getPrimaryCtaConfig(data?.ui_attributes),
  };
};

const getSubscriptionPlanConfig = (data: any): SubscriptionPlanConfig => ({
  subscriptionCard: {
    backgroundColor: data?.layout_ui?.subscription_card?.background_color ?? "",
    textColor: data?.layout_ui?.subscription_card?.text_color ?? "",
    iconColor: data?.layout_ui?.subscription_card?.icon_color ?? "",
    offerColor: data?.layout_ui?.subscription_card?.offer_color ?? "",
    highlightBackgroundColor: data?.layout_ui?.subscription_card?.highlight_background_color ?? "",
    highlightTextColor: data?.layout_ui?.subscription_card?.highlight_text_color ?? "",
  },
  tabNudge: data?.layout_ui?.tab_nudge ?? null,
  recommendedLabel: data?.layout_ui?.recommended_label ?? null,
  summaryTitles: Array.isArray(data?.layout_ui?.summary_titles)
    ? data.layout_ui.summary_titles.filter((line: any) => typeof line === "string" && line)
    : null,
  paymentTiles: Array.isArray(data?.payment_tiles) ? data.payment_tiles : [],
});

const getPaymentTileConfig = (data: any) => {
  const backgroundColor = data?.layout_ui?.payment_card?.background_color ?? "";
  return {
    layout: data?.layout ?? "ICONS",
    layoutUI: {
      paymentCard: {
        backgroundColor,
        borderColor: data?.layout_ui?.payment_card?.border_color ?? "",
        textColor: data?.layout_ui?.payment_card?.text_color ?? "",
        iconColor: data?.layout_ui?.payment_card?.icon_color ?? "",
        subtitleColor: data?.layout_ui?.payment_card?.subtitle_color ?? "",
        dividerColor: getTextColorByBrightness({
          bgHexColor: backgroundColor || "#FFFFFF",
          lightOpacity: 10,
          darkOpacity: 6,
        }),
        borderRadius: data?.layout_ui?.payment_card?.border_radius ?? PAYMENT_CARD_RADIUS_FALLBACK,
      },
      offerBadge: {
        backgroundColor: data?.layout_ui?.offer_badge?.background_color ?? "",
        textColor: data?.layout_ui?.offer_badge?.text_color ?? "",
      },
      paymentTileGap: data?.layout_ui?.payment_tile_gap ?? "",
    },
    modeDetails: data?.mode_details ?? [],
  };
};

export const buildBannerConfig = (items: any[], settings: any) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const multipleBannersEnabled = settings?.multiple_banners_enabled ?? false;
  const grouped = items.reduce((acc: Record<string, any[]>, item: any) => {
    const type = item?.type ?? "";
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});
  return Object.entries(grouped).map(([type, banners]) => {
    const mappedBanners = banners.map((b: any) => ({
      position: b?.position ?? 0,
      backgroundColor: b?.background_color ?? "",
      textColor: b?.text_color ?? "",
      htmlTextString: b?.html_text_string ?? "",
    }));
    return {
      type,
      enabled: settings?.enabled ?? banners[0]?.enabled ?? false,
      multipleBannersEnabled,
      animationStyle: settings?.animation_style ?? "vertical_ticker",
      animationDelay: settings?.animation_delay ?? 1,
      banners: mappedBanners,
      backgroundColor: mappedBanners[0]?.backgroundColor ?? "",
      textColor: mappedBanners[0]?.textColor ?? "",
      htmlTextString: mappedBanners[0]?.htmlTextString ?? "",
    };
  });
};
